import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthApiError } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Button, ButtonLink } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Banner } from '@/components/ui/Banner';
import { clearIdleTimeoutOverride } from '@/lib/idleTimeoutOverride';
import {
  consumeSessionExpiredNotice,
  consumeSignInReturnTo,
  sanitizeReturnTo,
} from '@/lib/sessionExpiry';
import toast from 'react-hot-toast';

/**
 * Email + password sign-in. The auth hook (FND-4) bakes JWT claims so the
 * sidebar / RLS get clinic memberships immediately on the next render.
 */
export function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sessionExpired] = useState(() => {
    clearIdleTimeoutOverride();
    const expired = consumeSessionExpiredNotice();
    if (expired) toast.dismiss();
    return expired;
  });
  const [storedReturnTo] = useState(() => consumeSignInReturnTo());

  const fromState = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)
    ?.from;
  const from = sanitizeReturnTo(
    fromState?.pathname
      ? `${fromState.pathname}${fromState.search ?? ''}${fromState.hash ?? ''}`
      : storedReturnTo,
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === 'invalid_credentials' || err.status === 400) {
          setError(t('auth.errors.invalid_credentials'));
        } else if (err.status === 429) {
          setError(t('auth.errors.too_many_requests'));
        } else {
          setError(t('auth.errors.unknown'));
        }
      } else {
        setError(t('auth.errors.unknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.sign_in.title')}
      subtitle={t('auth.sign_in.subtitle')}
      footer={
        <>
          {t('auth.sign_in.no_account')}{' '}
          <Link to="/create-clinic" className="font-medium text-ds-teal hover:text-ds-teal-700">
            {t('auth.sign_in.create_account')}
          </Link>
        </>
      }
    >
      {sessionExpired && (
        <Banner tone="warning" className="mb-6">
          {t(
            'auth.sign_in.session_expired',
            'Du er blevet logget ud på grund af inaktivitet',
          )}
        </Banner>
      )}
      {error && <ErrorAlert message={error} />}
      {/* method="post": a native submit (e.g. before the handler runs) must never put the
 credentials in the URL; handleSubmit always prevents it. */}
      <form method="post" action="/sign-in" onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('auth.sign_in.email')}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordInput
          label={t('auth.sign_in.password')}
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex items-center justify-end">
          <ButtonLink to="/forgot-password" variant="link">
            {t('auth.sign_in.forgot_password')}
          </ButtonLink>
        </div>

        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.sign_in.submitting') : t('auth.sign_in.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
